/**
 * Angular Alert Component
 *
 * This is a migrated version of the AngularJS acme-alert component.
 * It demonstrates the migration pattern for components.
 *
 * Original: ui/src/app/common/components/alert/alert.component.ts
 *
 * Usage in Angular templates:
 *   <app-alert type="warning">Warning message</app-alert>
 *   <app-alert type="info" icon="bell">Custom icon</app-alert>
 *
 * Usage in AngularJS templates (after downgrade):
 *   <ng-alert type="warning">Warning message</ng-alert>
 */
import { Component, Input, computed, signal } from '@angular/core';
import { NgClass, NgIf } from '@angular/common';

export type AlertType = 'default' | 'info' | 'warning' | 'error' | 'success';

@Component({
  selector: 'app-alert',
  standalone: true,
  imports: [NgIf, NgClass],
  template: require('./alert.component.html'),
  styles: [require('./alert.component.less')],
  host: { '[class]': 'hostClass()' },
})
export class AlertComponent {
  private static readonly ICONS: Record<string, string> = {
    info: 'fa fa-lg fa-info-circle',
    warning: 'fa fa-lg fa-exclamation-circle',
    error: 'fa fa-lg fa-exclamation-circle',
    success: 'fa fa-lg fa-check-circle',
  };

  private readonly _type = signal<AlertType>('default');
  @Input()
  get type(): AlertType { return this._type(); }
  set type(value: AlertType) { this._type.set(value); }

  private readonly _icon = signal<string | undefined>(undefined);
  @Input()
  get icon(): string | undefined { return this._icon(); }
  set icon(value: string | undefined) { this._icon.set(value); }

  readonly hostClass = computed(() => `acme-alert acme-alert--${this._type()}`);

  readonly iconClass = computed(() => {
    const icon = this._icon();
    if (icon) return `fa fa-lg fa-${icon}`;
    return AlertComponent.ICONS[this._type()] ?? null;
  });
}
